import { QuestionnaireType } from "@/components/controls/Questionnaires/Questionnaire";
import { SingleSelectOption } from "@/components/controls/SingleSelect";
import { DealFilters } from "@/pages/deals/components/DealsToolbar";
import { WorkflowItem } from "@/pages/settings/components/dealsSettings/StageStudio/workflow/DraggableWorkflowItem";
import type { DreamerFlow } from "@/pages/settings/components/dealsSettings/StageStudio/workflow/hooks/useAutofillApi";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import { PersonData } from "@/entities/people/types";
import {
  APILookupValue,
  APIObject,
  EntitiesResponse,
  EntityPage,
  ExtendedUseQueryResult,
  GenericAggregations,
  QueryData
} from "@/services/api/types";
import { ISOStringFormat } from "date-fns";
import { DealMilestone } from "@/components/shared/DealMilestone";

export enum DealColumn {
  NAME = "name",
  COMPANY_NAME = "companyName",
  ARR_MRR = "arrMrr", // Will be dynamically determined based on is_arr setting
  // The terms-currency half of ARR/MRR. Like ARR_MRR itself this is a
  // placeholder: it resolves to arrLocal or mrrLocal per the is_arr setting.
  ARR_MRR_TERMS = "arrMrrLocal",
  STATUS = "status",
  STAGE = "stage",
  ALERTS = "alerts", // UI-only — see DEALS_EXPORT_EXCLUDED_COLUMNS
  EXPECTED_CLOSE_AT = "expectedCloseAt",
  CLOSED_AT = "closedAt",
  CLOSED_LOST_REASON_NAME = "closedLostReasonName",
  CLOSING_QUARTER = "closingQuarter",
  KEY_STAKEHOLDERS_MAPPING_TEXT = "keyStakeholdersMappingText",
  CHAMPIONS_MAPPING_TEXT = "championsMappingText",
  QUALIFICATION_TEXT = "qualificationText",
  STAKEHOLDERS_APPROVAL_TEXT = "stakeholdersApprovalText",
  POC_TEXT = "pocText",
  DEAL_VALUE_AMOUNT = "dealValueAmount",
  AE_CONFIDENCE_LEVEL = "aeConfidenceLevel",
  PRODUCT_NAME = "productName",
  MANAGED_BY = "managedBy",
  CREATED_AT = "createdAt",
  LAST_ACTIVITY = "lastActivity",
  NEXT_STEP_TEXT = "nextStepText",
  CONTRACTS_CARR = "contractsCarr",
  // These two ids read as the deal's own fields but are not: the columns they
  // name read the contracts* rollups, same as CONTRACTS_CARR. The ids are
  // load-bearing — users' saved column layouts are keyed on them — so they
  // stay as they are.
  CONTRACTS_TCV = "totalContractValue",
  CONTRACTS_ACV = "annualContractValue",
  // The terms-currency half of each contract-derived money pair. The id is the
  // camelCased registry field key, so the built-in column owns it and the
  // registry field of the same key is not also mapped as a custom column.
  CONTRACTS_CARR_TERMS = "contractsCarrLocal",
  CONTRACTS_TCV_TERMS = "contractsTotalContractValueLocal",
  CONTRACTS_ACV_TERMS = "contractsAnnualContractValueLocal"
}

// Columns that are visible in the deals table UI but should not appear in the
// CSV export — alerts is a per-user UI affordance; the others are raw ID/blob
// fields with no useful CSV representation.
export const DEALS_EXPORT_EXCLUDED_COLUMNS: readonly string[] = [
  DealColumn.ALERTS,
  "uploadContracts",
  "contractId",
  "allDealPeople",
  "allDealPartnerContacts",
  "manuallyAddedPartnerContacts",
  "automaticallyAddedPartnerContacts"
];

export enum DealStatus {
  IN_PROGRESS = 1,
  STUCK = 2,
  CLOSED_WON = 4,
  CLOSED_LOST = 5
}

enum DealTechEvaluationStatus {
  NOT_STARTED = 1,
  IN_PROGRESS,
  COMPLETED
}

export enum DealConfidenceLevel {
  LOW = 1,
  MEDIUM,
  HIGH,
  COMMITTED
}

export enum CsmRenewalConfidenceLevel {
  LOW = 1,
  MEDIUM,
  HIGH
}

// Re-exported so existing importers keep working; the enum lives beside the design-system components that read it.
// eslint-disable-next-line react-refresh/only-export-components -- re-exported enum, not a component
export * from "@/components/shared/DealMilestone";

export enum DealContractDuration {
  ONE_MONTH = 1,
  ONE_QUARTER,
  ONE_YEAR,
  TWO_YEARS,
  THREE_YEARS,
  OTHER
}

interface AlertData {
  id: string;
  alert: string;
  createdAt: ISOStringFormat;
  isDismissed: boolean;
  targetUserId: string;
  dismissedBy: string;
}

interface DealSummary {
  dealId: string;
  overviewParagraph: string;
  id: string;
  createdAt: ISOStringFormat | null;
}

interface DealData extends APIObject {
  name: string;
  managedBy: string;
  companyId: string;
  companyName?: string;
  contactId: string;
  status: DealStatus | null;
  statusResolved?: string | null;
  stage: number | null;
  expectedCloseAt: ISOStringFormat | null;
  dealValueAmount: number | null;
  arr: number | null;
  mrr: number | null;
  arrLocal: number | null;
  mrrLocal: number | null;
  probability: number | null;
  openForDays: number | null;
  daysInStage: number | null;
  techEvaluationStatus: DealTechEvaluationStatus | null;
  qualificationScore: number | null;
  aeConfidenceLevel: DealConfidenceLevel | null;
  aeConfidenceLevelResolved?: string | null;
  aiAssignedProbability: number | null;
  lastActivity: ISOStringFormat | null;
  typeOfLastActivity: number | null;
  closedLostReason?: number | null;
  closedLostReasonText?: string | null;
  closedAt?: ISOStringFormat | null;
  lastModifiedBy?: string | null;
  createdAt: ISOStringFormat | null;
  keyStakeholdersMappingScore?: number | null;
  keyStakeholdersMappingText?: string | null;
  championsMappingScore?: number | null;
  championsMappingText?: string | null;
  qualificationText?: string | null;
  stakeholdersApprovalText?: string | null;
  pocText?: string | null;
  percentageFromOwnerPipelineArr?: number | null;
  percentageFromOwnerPipelineMrr?: number | null;
  percentageOfMappedContacts?: number | null;
  alerts?: AlertData[] | null;
  allDealPeople?: string[] | null;
  role?: string | null;
  manuallyAddedPeople?: string[] | null;
  automaticallyAddedPeople?: string[] | null;
  partnerId?: string | null;
  allDealPartnerContacts?: string[] | null;
  manuallyAddedPartnerContacts?: string[] | null;
  automaticallyAddedPartnerContacts?: string[] | null;
  hasNextSteps?: boolean;
  nextStepText?: string | null;
  productId?: string | null;
  productName?: string | null;
  contractId?: string | null;
  // The contract-derived rollups arrive under the linked-field keys
  // (contracts_*), resolved from the deal's contract at read time. Each is a
  // pair: the bare key carries the figure converted to the account's currency,
  // the `Local` key the figure in the currency the contract was signed in.
  contractsCarr?: number | null;
  contractsCarrLocal?: number | null;
  contractsCmrr?: number | null;
  contractsCmrrLocal?: number | null;
  contractsTotalContractValue?: number | null;
  contractsTotalContractValueLocal?: number | null;
  contractsAnnualContractValue?: number | null;
  contractsAnnualContractValueLocal?: number | null;
  // The unprefixed totalContractValue/annualContractValue are the deal's own
  // columns and have no _local counterpart in the payload, so they cannot be
  // split.
  totalContractValue?: number | null;
  annualContractValue?: number | null;
  contractsCurrency?: string | null;
  // The deal's own signing currency, distinct from contractsCurrency above.
  // Declared because APIObject's index signature otherwise types it `any`,
  // which let every comparison against it pass the typechecker unexamined.
  currency?: string | null;
}

interface DealCloseData {
  reason?: number;
  comment?: string;
}

interface ConvertToContractData {
  contractName: string;
  companyId: string;
  productId: string;
  currency: string;
  contractSignDate?: string | null;
  aeOwner?: string | null;
  csmOwner?: string | null;
}

interface DealsMe {
  quota: number | null;
  pipelineCoverage: number | null;
  committedArr: number | null;
  committedMrr: number | null;
}

type QuestionValueType = boolean | string | string[] | null;

type Score = {
  scoreText: string;
  scoreResult: number;
  reason: string | null;
};

type ResponseValue = {
  value: QuestionValueType;
  activityId?: string;
  referenceQuote?: string;
  citation?: string;
  person?: PersonData;
  candidateId?: string;
  candidateName?: string;
};

type Revision = {
  id: string;
  response: ResponseValue;
  createdAt: string;
  lastEditedBy?: string;
};

type QuestionResponse = {
  response: ResponseValue;
  innerValue?: QuestionValueType;
  person?: PersonData;
  revisions?: Revision[];
  createdAt?: string;
  lastEditedBy?: string;
};

type Responses = {
  [questionId: string]: QuestionResponse | null;
};

interface DealsQuestionnairesResults {
  responses: Responses;
  score: Score | null;
}

interface DealsQuestionnairesScores {
  scores: (Score & {
    questionnaireId: string;
    name: string;
    kpiValue: string | null;
  })[];
}

interface DealQuestionnaireQuestionSettings {
  id: string;
  fieldName: string | null;
  questionText: string;
  questionnaireId: string;
  responseType: {
    value: string;
    type: string | null;
    setValueType: string | number | null;
    unit: string | null;
    placeholder?: string;
    options?: SingleSelectOption[];
    textAreaQuestion?: string;
    // Additional properties for new field types
    minValue?: number;
    maxValue?: number;
    option1?: string;
    option2?: string;
    allowUsersToAdd?: boolean;
    checkboxInlineText?: string;
  };
  questionOrder: number | null;
  isMandatory: boolean;
  isSuccessCriteria: boolean | null;
  section: string | null;
  tooltipText: string | null;
  isSystem: boolean | null;
  /** Read-only: whether a hardcoded connector fills this question. */
  hasAutofillConnector?: boolean;
  relatedQuestionId: string | null;
  filledByDreamer: boolean | null;
  dreamerFlow?: DreamerFlow | null;
  dealId?: string | null;
}

interface DealQuestionnaire {
  id: string;
  name: string;
  typeValue: QuestionnaireType;
  questionnaireOrder: number;
  kpiValue: string | null;
  relatedQuestionnaireId: string | null;
  questions: DealQuestionnaireQuestionSettings[] | null;
  isGuardrails: boolean | null;
  isSuccessCriteriaEnabled?: boolean | null;
  description: string | null;
  isSystem: boolean | null;
  lastModifiedAt: ISOStringFormat | null;
  lastModifiedBy: string | null;
  isDeleted: boolean | null;
  stageId: number | null;
  stageName: string | null;
}

interface DealsQuestionnairesSettings {
  questionnaires: DealQuestionnaire[];
}

type DealQuestionnaireSettings = {
  [stageId: number]: DealsQuestionnairesSettingsResponse;
};

type DealQuestionnaireResults = {
  [questionnaireId: string]: DealsQuestionnairesResultsResponse;
};

type DealQuestionnaireScores = {
  [stageId: number]: DealsQuestionnairesScoresResponse;
};

type QuestionnaireQuestionOption = {
  id: string;
  option: string;
  questionId: string;
};

type DealQuestionnaireQuestionOptions = QuestionnaireQuestionOption[];

type DealsAggregationFieldNames =
  | "dealValueAmount"
  | "arr"
  | "mrr"
  | "daysInStage";

interface NextStep {
  id: string;
  createdAt: ISOStringFormat | null;
  entityId: string;
  description: string | null;
  citation: string | null;
  lastEditedBy?: string;
  isDeleted?: boolean;
}

interface NextStepsResponse {
  nextSteps: NextStep[];
}

interface Period {
  id: string;
  startMonth: number | null;
  endMonth: number | null;
  amount: number | null;
}

interface Commercials {
  id: string | null;
  dealId: string | null;
  contractId: string | null;
  productId: string | null;
  currency: string | null;
  contractStartDate: ISOStringFormat | null;
  contractEndDate: ISOStringFormat | null;
  optOutDate: ISOStringFormat | null;
  contractTotalAmount: number | null;
  amountBreakdownByPeriod: { periods: Period[] } | null;
  autoRenewal: boolean | null;
  licenseId: string | null;
  licensesInDeal: number | null;
  potentialLicensesInCompany: number | null;
  implementationCost: number | null;
  trainingCost: number | null;
  mrr: number | null;
  arr: number | null;
  mrrLocal: number | null;
  arrLocal: number | null;
  annualContractValue: number | null;
  totalContractValue: number | null;
  uploadContracts: { contracts: SavedAttachmentInfo[] } | null;
  isActive?: boolean | null;
  endYear?: number;
}

type DealsPage = EntityPage<DealData, DealsAggregationFieldNames>;
type DealsAggregations = GenericAggregations<DealsAggregationFieldNames>;
type DealsQueryData = QueryData<DealData, DealFilters>;
type DealsResponse = EntitiesResponse<DealData>;
type DealQueryResult = ExtendedUseQueryResult<DealsPage>;
type DealsMeResponse = ExtendedUseQueryResult<DealsMe>;
type DealsQuestionnairesSettingsResponse =
  ExtendedUseQueryResult<DealsQuestionnairesSettings>;
type DealsQuestionnairesResultsResponse =
  ExtendedUseQueryResult<DealsQuestionnairesResults>;
type DealsQuestionnairesScoresResponse =
  ExtendedUseQueryResult<DealsQuestionnairesScores>;
type DealQuestionnaireQuestionOptionsResponse =
  ExtendedUseQueryResult<DealQuestionnaireQuestionOptions>;
type DealNextStepsResponse = ExtendedUseQueryResult<NextStepsResponse>;
type DealCommercialsResponse = ExtendedUseQueryResult<Commercials>;
type DealSummaryQueryResponse = ExtendedUseQueryResult<DealSummary>;

interface AlertsBatchResponse {
  alerts: { [dealId: string]: AlertData[] };
}

interface DealStage {
  id: number;
  name: string;
  stageOrder: number;
  milestone: DealMilestone;
  questionnaires?: WorkflowItem[];
  dealsCount?: number;
}

interface DealStagesResponse {
  stages: DealStage[];
}

interface DealMilestonesResponse {
  milestones: APILookupValue[];
}

interface DealsQuestionnaireData extends DealQuestionnaire {
  responses?: Responses;
  score?: Score | null;
}

export enum DealsQuestionType {
  BOOLEAN = "bool",
  TEXT = "text",
  TEXTAREA = "textarea",
  SELECT = "select",
  PEOPLE = "people",
  CHECKBOX_WITH_REQUIRED_TEXT = "checkboxWithRequiredText",
  OPTIONS = "multiSelect",
  NUMBER = "number",
  PERCENTAGE = "percentage",
  DATE = "date",
  DATE_RANGE = "dateRange",
  EMAIL = "email",
  URL = "url",
  CURRENCY = "currency",
  SWITCH = "switch",
  CHECKBOX = "checkbox"
}

// Defined for future use
// interface DealsQuestionSelectOption {
//   id: string;
//   value: string;
//   displayName: string;
//   subtitle?: string;
// }

// interface DealsQuestionInputProps {
//   value?: any;
//   onChange?: (value: any) => void;
//   question?: any;
// }

type CreateQuestionnaireQuestionSettings = Omit<
  DealQuestionnaireQuestionSettings,
  "id"
>;

export type {
  DealData,
  DealsPage,
  DealStage,
  DealStagesResponse,
  DealMilestonesResponse,
  DealsAggregations,
  DealsQueryData,
  DealsResponse,
  DealQueryResult,
  DealCloseData,
  ConvertToContractData,
  DealsMeResponse,
  DealsMe,
  DealQuestionnaire,
  DealsQuestionnaireData,
  DealsQuestionnairesResultsResponse,
  DealsQuestionnairesResults,
  DealQuestionnaireResults,
  DealsQuestionnairesSettingsResponse,
  DealQuestionnaireSettings,
  DealQuestionnaireQuestionSettings,
  DealQuestionnaireScores,
  DealsQuestionnairesScoresResponse,
  Responses,
  Score,
  QuestionValueType,
  ResponseValue,
  Revision,
  QuestionResponse,
  CreateQuestionnaireQuestionSettings,
  DealQuestionnaireQuestionOptionsResponse,
  QuestionnaireQuestionOption,
  DealNextStepsResponse,
  NextStepsResponse,
  NextStep,
  Commercials,
  DealCommercialsResponse,
  Period,
  AlertData,
  AlertsBatchResponse,
  DealSummary,
  DealSummaryQueryResponse
};

export interface QuestionnaireUpdateInput {
  name?: string;
  stageId?: number;
  questionnaireOrder?: number;
  kpiValue?: string;
  typeValue?: string;
  relatedQuestionnaireId?: string;
  isGuardrails?: boolean;
  isSuccessCriteriaEnabled?: boolean | null;
  isDeleted?: boolean;
  description?: string | null;
}

export interface QuestionnaireBaseOutput extends DealQuestionnaire {}

export interface QuestionnaireReorderResponse {
  success: boolean;
  updatedOrder: string[];
}

export interface QuestionnaireQuestionsReorderResponse {
  success: boolean;
  updatedOrder: string[];
}

export type QuestionnaireByIdResponse =
  ExtendedUseQueryResult<QuestionnaireBaseOutput>;
export type QuestionnaireUpdateResponse =
  ExtendedUseQueryResult<QuestionnaireBaseOutput>;
